import { DEFAULT_RUNWAY_LAYOUTS } from './defaultRunwayLayouts'
import { createDefaultDashboardTabs } from './tabCatalogue'
import type {
    AirportCodes,
    ResolvedWidget,
    RunwayLayout,
    RunwayLayoutMode,
    StatusDashboardSettings,
    WidgetSettings,
} from './types'
import type { DashboardWidgetId, WidgetDefinition } from './widgetCatalogue'
import { DASHBOARD_WIDGETS } from './widgetCatalogue'

const DISABLED_WIDGET: ResolvedWidget = Object.freeze({ enabled: false, options: {} })

export const resolveWidget = (
    settings: StatusDashboardSettings | undefined,
    widgetId: DashboardWidgetId,
    airport: string | undefined,
): ResolvedWidget => {
    const widget = settings?.widgets?.[widgetId]
    if (!airport || !widget?.airports?.includes(airport)) {
        return DISABLED_WIDGET
    }
    return { enabled: true, options: { ...widget.options } }
}

export const isWidgetOptionOn = (widget: ResolvedWidget, optionKey: string): boolean =>
    widget.enabled && widget.options[optionKey] === true

export const hiddenWidgetOptions = (
    widget: ResolvedWidget,
    definition: WidgetDefinition | undefined,
): string[] =>
    (definition?.options ?? [])
        .map(option => option.key)
        .filter(key => !isWidgetOptionOn(widget, key))

export const createVisibleWidgetSettings = (
    definition: WidgetDefinition,
    airports: AirportCodes,
): WidgetSettings => ({
    airports: [...airports],
    options: Object.fromEntries(definition.options.map(option => [option.key, true])),
})

export const createVisibleDashboardSettings = (
    airports: AirportCodes,
): StatusDashboardSettings => ({
    allowAdminDismissWarning: false,
    tabs: createDefaultDashboardTabs(airports),
    widgets: Object.fromEntries(
        DASHBOARD_WIDGETS.map(definition => [
            definition.id,
            createVisibleWidgetSettings(definition, airports),
        ]),
    ),
})

export const RUNWAY_LAYOUT_MODES: readonly RunwayLayoutMode[] = [
    'arrival',
    'departure',
    'conditional',
]

export const resolveRunwayLayout = (
    settings: StatusDashboardSettings | undefined,
    airport: string | undefined,
): RunwayLayout | undefined => (airport ? settings?.runwayLayouts?.[airport] : undefined)

export const defaultRunwayLayoutsFor = (airports: AirportCodes): Record<string, RunwayLayout> =>
    Object.fromEntries(
        airports
            .filter(airport => DEFAULT_RUNWAY_LAYOUTS[airport])
            .map(airport => [airport, DEFAULT_RUNWAY_LAYOUTS[airport]]),
    )
